/**
 * usePaginatedCache — shared SWR-style hook for paginated lists with
 * viewport-aware visible-slice rendering.
 *
 * Used by sessions and projects sidebar lists. Same shape, different
 * fetch function + cache key.
 *
 * Design:
 *   - On mount, hydrate items from localStorage cache (instant first paint).
 *   - Simultaneously fetch fresh page 1 from backend; reconcile when it
 *     arrives. This is stale-while-revalidate.
 *   - viewbar shows items.slice(0, visibleCount). visibleCount is derived
 *     from window.innerHeight / rowHeight * visibleBuffer (recomputes on
 *     resize). Click "load more" → bump visibleCount.
 *   - When cached items in memory run low (items.length - visibleCount <
 *     prefetchThreshold) AND backend has more (next_cursor present), the
 *     hook fires the next backend page in the background so the next
 *     load-more click is instant.
 *   - Mutations (setItems / prepend / patch / remove) write through to
 *     localStorage so reload reflects the latest state.
 *
 * Cache key MUST be user-scoped (e.g. `chatty_session_cache_<sub>`) so
 * different users on a shared device don't see each other's lists.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_ROW_HEIGHT = 56; // px — sidebar row visual height
const DEFAULT_VISIBLE_BUFFER = 1.5;
const DEFAULT_PREFETCH_THRESHOLD = 20;
const DEFAULT_PAGE_SIZE = 50;

function readCache(key) {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
}

function writeCache(key, items) {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch (_) {
    // localStorage may be full or unavailable in private mode; cache is
    // an optimization, not load-bearing — skip silently.
  }
}

export function clearPaginatedCache(key) {
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch (_) { /* same as writeCache */ }
}

function computeVisibleCount(rowHeight, visibleBuffer) {
  const h = (typeof window !== 'undefined' && window.innerHeight) || 600;
  const fit = Math.max(1, Math.ceil(h / rowHeight));
  return Math.ceil(fit * visibleBuffer);
}

export default function usePaginatedCache({
  cacheKey,
  fetchPage,        // async ({ limit, cursor }) => { items: [], next_cursor }
  enabled = true,
  pageSize = DEFAULT_PAGE_SIZE,
  rowHeight = DEFAULT_ROW_HEIGHT,
  visibleBuffer = DEFAULT_VISIBLE_BUFFER,
  prefetchThreshold = DEFAULT_PREFETCH_THRESHOLD,
}) {
  const [items, setItemsState] = useState(() => readCache(cacheKey) || []);
  const [nextCursor, setNextCursor] = useState(null);
  const [visibleCount, setVisibleCount] = useState(() =>
    computeVisibleCount(rowHeight, visibleBuffer)
  );
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const cacheKeyRef = useRef(cacheKey);
  const fetchInFlightRef = useRef(false);
  // Bump to trigger an explicit refetch (delete/rename callers).
  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  useEffect(() => {
    cacheKeyRef.current = cacheKey;
  }, [cacheKey]);

  // Setter that also persists to localStorage.
  const setItems = useCallback((updater) => {
    setItemsState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      writeCache(cacheKeyRef.current, next);
      return next;
    });
  }, []);

  // Recompute visibleCount on viewport resize.
  useEffect(() => {
    const onResize = () => setVisibleCount(computeVisibleCount(rowHeight, visibleBuffer));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [rowHeight, visibleBuffer]);

  // Initial SWR fetch: cache hydrate + fresh page 1. Re-runs when
  // refreshTick bumps (explicit refresh) or cacheKey/enabled change.
  useEffect(() => {
    if (!enabled || !cacheKey) return undefined;
    let cancelled = false;
    const cached = readCache(cacheKey);
    if (cached) setItemsState(cached);
    setLoading(true);
    fetchPage({ limit: pageSize, cursor: null })
      .then((response) => {
        if (cancelled) return;
        const fresh = response.items || [];
        const cursor = response.next_cursor || null;
        setItems(fresh);
        setNextCursor(cursor);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(`usePaginatedCache initial fetch failed for ${cacheKey}:`, err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [enabled, cacheKey, fetchPage, pageSize, setItems, refreshTick]);

  // Fetch the next backend page (only when there's a cursor).
  const fetchNext = useCallback(async () => {
    if (!nextCursor || fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    setLoadingMore(true);
    try {
      const response = await fetchPage({ limit: pageSize, cursor: nextCursor });
      const fresh = response.items || [];
      const cursor = response.next_cursor || null;
      setItems((prev) => [...prev, ...fresh]);
      setNextCursor(cursor);
    } catch (err) {
      console.error(`usePaginatedCache load-more failed for ${cacheKeyRef.current}:`, err);
    } finally {
      fetchInFlightRef.current = false;
      setLoadingMore(false);
    }
  }, [nextCursor, fetchPage, pageSize, setItems]);

  // Bump visibleCount; if approaching cache end and backend has more,
  // prefetch in the background so the next click is instant.
  const loadMore = useCallback(() => {
    if (loading) return;
    const bump = computeVisibleCount(rowHeight, visibleBuffer);
    setVisibleCount((c) => c + bump);
    if (items.length - visibleCount < prefetchThreshold && nextCursor) {
      fetchNext();
    }
  }, [
    loading,
    items.length,
    visibleCount,
    prefetchThreshold,
    nextCursor,
    fetchNext,
    rowHeight,
    visibleBuffer,
  ]);

  const visible = useMemo(() => items.slice(0, visibleCount), [items, visibleCount]);

  // hasMore is true when:
  //   - cache has items beyond what's visible, OR
  //   - backend has more (next_cursor present)
  const hasMore = useMemo(
    () => visibleCount < items.length || nextCursor !== null,
    [visibleCount, items.length, nextCursor]
  );

  return {
    items,
    setItems,
    visible,
    visibleCount,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    refresh,
  };
}
